import React, { useMemo, useState, useEffect, lazy } from "react";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { connect } from "react-redux";
import { createHashRouter, Navigate,createBrowserRouter,
    RouterProvider,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType } from "react-router-dom";
import * as Constants from "../src/constants/Constants";
import { AppContext } from "./components/index";
import MainLayout from "./layouts/MainLayout";
import { getTenantData } from "./layouts/mainLayoutLoader";
import "./App.css";
import { getAllLocations } from "./modules/home/services/HomeServices";

// At the top of the file, update imports to use lazy loading
const CheckOut = lazy(() => import("../src/modules/checkout/screen/CheckOut"));
const Home = lazy(() => import("../src/modules/home/screen/Home"));
const OrderHistory = lazy(() => import("../src/modules/order-history/screen/OrderHistory"));
const PageNotFound = lazy(() => import("../src/modules/unauthorize/screen/PageNotFound"));
const ResetPassword = lazy(() => import("./modules/auth/screens/ResetPassword"));
const Category = lazy(() => import("./modules/category/screen/Category"));
const Product = lazy(() => import("./modules/product/screen/Product"));
const Profile = lazy(() => import("./modules/profile/screen/Profile"));

// ✅ Function to get tenant name from both URL patterns
const getTenantName = () => {
    const tenantFromStorage = localStorage.getItem("tenant");
    const urlTenant = window.location.search.slice(1).split("/");
    const isValidUrlTenant = urlTenant[0] && urlTenant[0] !== "undefined";

    if (tenantFromStorage) {
        if (
            isValidUrlTenant &&
            urlTenant[0] === "app" &&
            urlTenant[1] === "build"
        ) {
            const tenantFromUrl = urlTenant[2];
            if (tenantFromUrl !== tenantFromStorage) {
                localStorage.removeItem("tenant");
            }
            return tenantFromUrl;
        } else if (isValidUrlTenant) {
            return window.location.search.slice(1);
        } else {
            return tenantFromStorage;
        }
    } else {
        localStorage.removeItem("tenant");
        if (isValidUrlTenant) {
            return window.location.search.slice(1);
        }
    }
    return "";
};

// ✅ Update TenantRedirect component
const TenantRedirect = () => {
    const tenant = useMemo(getTenantName, []);
    localStorage.setItem("tenant", tenant);

    if (!tenant) {
        return <Navigate to={Constants.NOT_FOUND_ERROR_PAGE} replace />;
    }

    // Only redirect if we're not already on the app/build route
    if (!window.location.pathname.startsWith("/app/build")) {
        return <Navigate to={`/app/build/#/${tenant}`} replace />;
    }

    return <Navigate to={`/${tenant}`} replace />;
};

Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN, // Your Sentry DSN goes here
    environment: process.env.REACT_APP_ENV, // Your Sentry Environment goes here
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useState,
        useLocation,
        useNavigationType,
        createBrowserRouter,
        RouterProvider,
        createHashRouter,
        createRoutesFromChildren,
        matchRoutes
      }),
      Sentry.extraErrorDataIntegration() // Capture additional error data
    ],
    tracesSampleRate: 0.2, // Capture 20% of transactions
  });

const router = createHashRouter([
    {
        index: true,
        element: <TenantRedirect />,
        errorElement: <Navigate to="/404" replace />,
    },
    {
        path: `/:tenant`,
        // loader: getAllLocations,
        element: <Home />,
    },
    {
        path: Constants.RESET_PASSWORD_PATH.slice(1),
        element: <ResetPassword />,
    },
    {
        path: "/:tenant",
        element: <MainLayout />,
        loader: getTenantData,
        children: [
            {
                path: Constants.PRODUCT_PATH.slice(1),
                element: <Product />,
            },
            {
                index: true,
                path: Constants.CATEGORY_PATH.slice(1),
                element: <Category />,
            },
            {
                path: Constants.CHECKOUT_PATH.slice(1),
                element: <CheckOut />,
            },
            {
                path: Constants.ORDER_HISTORY_PATH.slice(1),
                element: <OrderHistory />,
            },
            {
                path: Constants.PROFILE_PATH.slice(1),
                element: <Profile />,
            },
        ],
    },
    {
        path: "*",
        element: <PageNotFound />,
    },
    {
        path: "404",
        element: <PageNotFound />,
    },
]);

const App = () => {
    const [loading, setLoading] = useState(false);
    
    // Remove duplicate useEffect
    useEffect(() => {
        let timeoutId;
        const loader = document.getElementById('initial-loader');
        
        if (loader) {
            // Wait for the animation to complete before removing
            timeoutId = setTimeout(() => {
                loader.style.opacity = '0';
                setTimeout(() => {
                    loader.remove();
                }, 300);
            }, 3000); // Match this with the animation duration
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    const handleLoading = (item) => {
        setLoading(item);
    };

    const grubLoading = {
        loading: loading,
        handleLoading,
    };

    // Wrap RouterProvider in Suspense for better code splitting
    return (
        <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
            <AppContext.Provider value={grubLoading}>
                <React.Suspense fallback={
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh'
                    }}>
                        <div className="loader-spinner" />
                    </div>
                }>
                    <RouterProvider router={router} />
                </React.Suspense>
            </AppContext.Provider>
        </Sentry.ErrorBoundary>
    );
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps)(App);
